/**
 * Saga glue between mediaDetails edit actions and the worker-hosted Redux store.
 *
 * Re-initializes the selected-groups map whenever the worker emits fresh UDR or detection
 * cluster groups (OUT_* actions), and pushes local audio-redaction, voice-mask, and
 * background-blur edits into the worker via IN_SET_AUDIO_EDITS / IN_SET_BACKGROUND_BLUR_EDITS
 * (with undo-history descriptions for the remove-all cases). Also surfaces
 * WORKER_WARN_FAILURE messages as warning snackbars. Forked from the module root saga in
 * ./saga.ts; the worker actions come from src/common-app/web-worker (@worker).
 */
import { omitBy } from 'lodash';
import { sagaIntl } from '@i18n';
import {
  all,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';

import {
  IN_SET_AUDIO_EDITS,
  OUT_UDR_CLUSTER_GROUPS,
  OUT_DETECTION_CLUSTER_GROUPS,
  WORKER_WARN_FAILURE,
  WorkerWarnFailurePayload,
  IN_SET_BACKGROUND_BLUR_EDITS,
} from '@worker';
import {
  actionInitSetSelectedGroups,
  ADD_REDACTION_CODE_TO_TRANSCRIPTION,
  DELETE_NOTES,
  MASK_VOICE_WORDS,
  REDACT_SLICE,
  REDACT_WORDS,
  MASK_SLICE,
  REMOVE_REDACTION_CODE_FROM_TRANSCRIPTION,
  REMOVE_ALL_AUDIO_REDACTIONS,
  REMOVE_ALL_VOICE_MASK_EDITS,
  RESET_SLICE,
  RESET_WORDS,
  UPDATE_NOTES,
  UPDATE_BUFFER,
  DELETE_BUFFER,
  BACKGROUND_BLUR_SLICE,
  RESET_BACKGROUND_BLUR_SLICE,
  UPDATE_BACKGROUND_BLUR_LEVELS,
} from '../actions';
import {
  selectVoiceMaskEdits,
  selectAudioRedactions,
  selectSelected,
  selectBackgroundBlurEdits,
} from '../selectors';
import { enqueueSnackbar } from '@common-modules/snackbar';

export function* workerOutSaga() {
  yield* all([
    fork(onActionOutTimeline),
    fork(onActionAudioRedactionUpdate),
    fork(onActionBackgroundBlurUpdate),
    fork(WatchWorkerSaveAssetFailure),
  ]);
}

function* WatchWorkerSaveAssetFailure() {
  yield* takeEvery(WORKER_WARN_FAILURE, workerWarnFailure);
}

function* workerWarnFailure(action: { payload: WorkerWarnFailurePayload }) {
  const { message } = action.payload;
  yield* put(
    enqueueSnackbar({
      message,
      variant: 'warning',
    })
  );
}

function* onActionOutTimeline() {
  yield* takeEvery(
    [OUT_UDR_CLUSTER_GROUPS, OUT_DETECTION_CLUSTER_GROUPS],
    function* () {
      const selected = yield* select(selectSelected);
      yield* put(
        actionInitSetSelectedGroups({
          selected: omitBy(selected, (v) => v !== true),
        })
      );
    }
  );
}

function* onActionAudioRedactionUpdate() {
  yield* takeLatest(
    [
      REDACT_WORDS,
      RESET_WORDS,
      REDACT_SLICE,
      MASK_SLICE,
      RESET_SLICE,
      REMOVE_ALL_AUDIO_REDACTIONS,
      REMOVE_ALL_VOICE_MASK_EDITS,
      UPDATE_NOTES,
      DELETE_NOTES,
      UPDATE_BUFFER,
      DELETE_BUFFER,
      ADD_REDACTION_CODE_TO_TRANSCRIPTION,
      REMOVE_REDACTION_CODE_FROM_TRANSCRIPTION,
      MASK_VOICE_WORDS,
    ],
    function* (action) {
      const intl = sagaIntl();
      const audioRedactionEdits = yield* select(selectAudioRedactions);
      const voiceMaskEdits = yield* select(selectVoiceMaskEdits);
      const metadata =
        action.type === REMOVE_ALL_AUDIO_REDACTIONS.type
          ? {
              description: intl.formatMessage({
                id: 'removeAllAudioRedactionsHistory',
                defaultMessage: 'remove all audio redactions',
              }),
            }
          : action.type === REMOVE_ALL_VOICE_MASK_EDITS.type
            ? {
                description: intl.formatMessage({
                  id: 'removeAllVoiceMaskHistory',
                  defaultMessage: 'remove all voice mask',
                }),
              }
            : undefined;
      yield* put(
        IN_SET_AUDIO_EDITS({ audioRedactionEdits, voiceMaskEdits, metadata })
      );
    }
  );
}

function* onActionBackgroundBlurUpdate() {
  yield* takeLatest(
    [
      BACKGROUND_BLUR_SLICE,
      RESET_BACKGROUND_BLUR_SLICE,
      UPDATE_BACKGROUND_BLUR_LEVELS,
      // REMOVE_ALL_BACKGROUND_BLUR, // not implemented
    ],
    function* () {
      const backgroundBlurEdits = yield* select(selectBackgroundBlurEdits);
      yield* put(IN_SET_BACKGROUND_BLUR_EDITS({ backgroundBlurEdits }));
    }
  );
}
